import { chartHasSeriesData } from "./ChartBlock";
import { facetChartHasSeriesData } from "./pricingChartsUtils";
import { buildProvinceAvgUnitPriceChart, buildRetailerSnapshotBars } from "./productSnapshotChartUtils";

/**
 * @param {object} params
 * @param {boolean} params.hasSelectedProduct
 * @param {object} [params.monthlyByProductCandlestickChart]
 * @param {object} params.monthlyByProductFacetChart
 * @param {object} [params.monthlyByProductCombinedChart]
 * @param {boolean} [params.productChartCombinedView]
 * @param {object} params.monthlyByRetailerChart
 * @param {object} [params.monthlyByRetailerCombinedChart]
 * @param {boolean} [params.retailerChartCombinedView]
 * @param {object} params.monthlyByProvinceChart
 * @param {object} [params.monthlyByProvinceCombinedChart]
 * @param {boolean} [params.provinceChartCombinedView]
 * @param {object} params.quarterlyChart
 */
export function pricingReportIncludedChartKeys({
  hasSelectedProduct,
  monthlyByProductFacetChart,
  monthlyByProductCandlestickChart,
  monthlyByProductCombinedChart,
  productChartCombinedView = false,
  monthlyByRetailerChart,
  monthlyByRetailerCombinedChart,
  retailerChartCombinedView = false,
  monthlyByProvinceChart,
  monthlyByProvinceCombinedChart,
  provinceChartCombinedView = false,
  quarterlyChart,
}) {
  const keys = [];

  const productChartHasData = productChartCombinedView
    ? chartHasSeriesData(monthlyByProductCombinedChart)
    : hasSelectedProduct
      ? chartHasSeriesData(monthlyByProductCandlestickChart)
      : facetChartHasSeriesData(monthlyByProductFacetChart);
  if (productChartHasData) {
    keys.push("monthly-product");
  }

  if (hasSelectedProduct) {
    const retailerChartHasData = retailerChartCombinedView
      ? chartHasSeriesData(monthlyByRetailerCombinedChart)
      : facetChartHasSeriesData(monthlyByRetailerChart);
    if (retailerChartHasData) {
      keys.push("monthly-retailer");
    }
    const provinceChartHasData = provinceChartCombinedView
      ? chartHasSeriesData(monthlyByProvinceCombinedChart)
      : facetChartHasSeriesData(monthlyByProvinceChart);
    if (provinceChartHasData) {
      keys.push("monthly-province");
    }
    if (chartHasSeriesData(quarterlyChart)) {
      keys.push("quarterly");
    }
  }

  return keys;
}

/**
 * @param {object} params
 * @param {boolean} params.productSnapshotIsVisible
 * @param {string[] | { name?: string }[]} [params.selectedRetailers]
 * @param {object[]} params.retailerSnapshotRows
 * @param {object[]} params.provinceSnapshotRows
 * @param {*} params.retailerSnapshotError
 * @param {*} params.provinceSnapshotError
 */
export function pricingReportIncludedSnapshotChartKeys({
  productSnapshotIsVisible,
  selectedRetailers = [],
  retailerSnapshotRows,
  provinceSnapshotRows,
  retailerSnapshotError,
  provinceSnapshotError,
}) {
  if (!productSnapshotIsVisible) {
    return [];
  }

  const keys = [];
  const retailerBars = buildRetailerSnapshotBars(retailerSnapshotRows);
  const provinceChart = buildProvinceAvgUnitPriceChart(provinceSnapshotRows);

  if (!selectedRetailers.length && !retailerSnapshotError && retailerBars.length > 0) {
    keys.push("snapshot-retailer");
  }

  if (!provinceSnapshotError && provinceChart.provinceChartHasAnyData) {
    keys.push("snapshot-province");
  }

  return keys;
}

export function pricingReportAllIncludedChartKeys(snapshotKeys, pricingKeys) {
  return [...snapshotKeys, ...pricingKeys];
}

/**
 * @param {object} params
 * @param {string[]} params.includedChartKeys
 * @param {boolean} params.hasSelectedProduct
 * @param {boolean} params.isMonthlyByProductLoading
 * @param {boolean} params.isMonthlyByRetailerLoading
 * @param {boolean} params.isMonthlyByProvinceLoading
 * @param {boolean} params.isQuarterlyLoading
 */
export function pricingReportIncludedChartsReady({
  includedChartKeys,
  hasSelectedProduct,
  isMonthlyByProductLoading,
  isMonthlyByRetailerLoading,
  isMonthlyByProvinceLoading,
  isQuarterlyLoading,
}) {
  const loadingByKey = {
    "monthly-product": isMonthlyByProductLoading,
    "monthly-retailer": isMonthlyByRetailerLoading,
    "monthly-province": isMonthlyByProvinceLoading,
    quarterly: isQuarterlyLoading,
  };

  for (const chartKey of includedChartKeys) {
    if (loadingByKey[chartKey]) {
      return false;
    }
  }

  if (hasSelectedProduct) {
    /* Keys not in includedChartKeys because not mounted are not blocking */
  }

  return true;
}

/**
 * @param {object} params
 */
export function pricingReportPrintIsDisabled({
  aggregateTotalGroupCount,
  startDateForApi,
  endDateForApi,
  dateFromValid,
  dateToValid,
  dateRangeValid,
  isPricingDataBusy,
  aggregateInitialLoading,
  isPricingPrintPreparing,
  pricingSummaryIsVisible = false,
  productSnapshotIsVisible,
  showProductSnapshotRetailerChart = true,
  isQuarterlySummaryLoading,
  quarterlySummaryStats,
  quarterlySummaryError,
  isRetailerSnapshotLoading,
  isProvinceSnapshotLoading,
  includedChartsReady,
  isCardCoversLoading = false,
}) {
  if (aggregateTotalGroupCount === 0) {
    return true;
  }

  if (!startDateForApi || !endDateForApi || !dateFromValid || !dateToValid || !dateRangeValid) {
    return true;
  }

  if (isPricingDataBusy || aggregateInitialLoading) {
    return true;
  }

  if (isPricingPrintPreparing) {
    return true;
  }

  if (
    pricingSummaryIsVisible &&
    isQuarterlySummaryLoading &&
    quarterlySummaryStats === undefined
  ) {
    return true;
  }

  if (pricingSummaryIsVisible && quarterlySummaryError) {
    return true;
  }

  if (
    productSnapshotIsVisible &&
    ((showProductSnapshotRetailerChart && isRetailerSnapshotLoading) ||
      isProvinceSnapshotLoading)
  ) {
    return true;
  }

  if (!includedChartsReady) {
    return true;
  }

  if (isCardCoversLoading) {
    return true;
  }

  return false;
}
